import { useT } from '@planetra/i18n';
import { Button, Callout } from '@planetra/ui';

export interface ErrorFallbackProps {
  /** Called to re-render the subtree that failed. Offered only when retrying
   *  can plausibly succeed — a chunk that failed to load, a transient fetch. */
  onRetry?: (() => void) | undefined;
  /** Full page reload. The escape hatch when the component tree itself is the
   *  thing that is wedged. */
  onReload?: (() => void) | undefined;
  titleKey?: 'errors:boundary.title' | 'errors:route.title' | undefined;
  descriptionKey?: 'errors:boundary.description' | 'errors:route.description' | undefined;
}

/**
 * What the user sees when a boundary catches something.
 *
 * Presentational, and shared by both boundaries so the design is identical
 * whether React Router or the class boundary caught it. PRD §15 requires these
 * states be designed rather than left to a generic toast, and specifically
 * that the user is told their work survives — a run already accepted by the
 * platform keeps going on the server whatever this tab does.
 *
 * No error message or stack is rendered. It would be untranslated, unhelpful,
 * and occasionally a disclosure; the reporter has the details.
 */
export function ErrorFallback({
  onRetry,
  onReload,
  titleKey = 'errors:boundary.title',
  descriptionKey = 'errors:boundary.description',
}: ErrorFallbackProps) {
  const { t } = useT();

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <Callout
        tone="danger"
        title={t(titleKey)}
        className="max-w-lg"
        action={
          onRetry === undefined && onReload === undefined ? undefined : (
            <div className="flex gap-2">
              {onRetry === undefined ? null : (
                <Button size="sm" onClick={onRetry}>
                  {t('common:actions.retry')}
                </Button>
              )}
              {onReload === undefined ? null : (
                <Button size="sm" variant="secondary" onClick={onReload}>
                  {t('common:actions.reload')}
                </Button>
              )}
            </div>
          )
        }
      >
        {t(descriptionKey)}
      </Callout>
    </div>
  );
}
